import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Input, Textarea } from "@threadws/jute";
import type { HttpError, PromptVersion } from "../../api/generated";
import { zCreatePromptVersion } from "../../api/generated/zod.gen";

/** Suggestions for the model field. Any Anthropic model id works. */
const MODELS = ["claude-sonnet-5-5", "claude-opus-5-5", "claude-haiku-5-5", "claude-fable-5-1"];

export interface PromptVersionValues {
  systemPrompt: string;
  model: string;
  note: string;
}

interface PromptVersionFormProps {
  /** The active version. The form starts from it, and follows it when it changes, keeping your edits. */
  active: PromptVersion;
  /** Saves a new version and makes it active. Throw an `HttpError` to show it on the form. */
  onSubmit: (values: PromptVersionValues) => Promise<unknown>;
}

// Generated from the API spec, so the limits match the server's.
const resolver = zodResolver(zCreatePromptVersion.pick({ systemPrompt: true, model: true, note: true }).required(), {
  error: (issue) => (issue.code === "too_small" ? "This is required" : undefined),
});

/** Edits the prompt and model. Saving adds a version rather than changing the old one. */
export function PromptVersionForm({ active, onSubmit }: PromptVersionFormProps) {
  const { register, handleSubmit, reset, setError, formState } = useForm<PromptVersionValues>({
    resolver,
    values: { systemPrompt: active.systemPrompt, model: active.model, note: "" },
    resetOptions: { keepDirtyValues: true },
  });
  const { errors, isSubmitting, isDirty } = formState;

  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit(values);
      reset({ ...values, note: "" });
    } catch (error) {
      setError("root.server", { message: (error as HttpError).message });
    }
  });

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <Textarea label="System prompt" rows={18} className="font-mono text-xs" {...register("systemPrompt")} />
        {errors.systemPrompt && <p className="text-sm text-danger">{errors.systemPrompt.message}</p>}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="Model"
          list="agent-models"
          helpText="An Anthropic model id"
          error={errors.model?.message}
          {...register("model", { setValueAs: (value: string) => value.trim() })}
        />
        <datalist id="agent-models">
          {MODELS.map((model) => (
            <option key={model} value={model} />
          ))}
        </datalist>
        <Input label="What changed" optional placeholder="e.g. Stricter about due dates" error={errors.note?.message} {...register("note")} />
      </div>
      {errors.root?.server && <p className="text-sm text-danger">{errors.root.server.message}</p>}
      <div className="flex items-center justify-end gap-3">
        <span className="text-xs text-text-secondary">Saving makes a new version and starts using it for new runs.</span>
        <Button type="submit" disabled={isSubmitting || !isDirty}>
          {isSubmitting ? "Saving…" : "Save new version"}
        </Button>
      </div>
    </form>
  );
}
